import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import { DeepPartial } from '~/types';

export type VehicleInfoContextType = {
  enablePriceObserver?: boolean;
  enableBreadcrumbObserver?: boolean;
  isCondensed?: boolean;
};

const defaultContext: VehicleInfoContextType = {
  enablePriceObserver: false,
  enableBreadcrumbObserver: false,
  isCondensed: false
};

const VehicleInfoContext =
  createContext<VehicleInfoContextType>(defaultContext);

export const generateVehicleInfoContext = (
  customState: DeepPartial<VehicleInfoContextType>
): VehicleInfoContextType =>
  deepmerge<VehicleInfoContextType, DeepPartial<VehicleInfoContextType>>(
    defaultContext,
    customState
  );

export function MockedVehicleInfoContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<VehicleInfoContextType>;
}>) {
  return (
    <VehicleInfoContext.Provider value={generateVehicleInfoContext(mockValue)}>
      {children}
    </VehicleInfoContext.Provider>
  );
}

type OwnProps = React.PropsWithChildren<{
  value: VehicleInfoContextType;
}>;

export function VehicleInfoProvider({ children, value }: OwnProps) {
  const contextValue = useMemo(
    () => generateVehicleInfoContext(value),
    [value]
  );

  return (
    <VehicleInfoContext.Provider value={contextValue}>
      {children}
    </VehicleInfoContext.Provider>
  );
}

VehicleInfoContext.displayName = 'VehicleInfoContext';

export default VehicleInfoContext;

export function useVehicleInfoContext() {
  return useContext(VehicleInfoContext);
}
